import { Box, TextField } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import { useDataProvider, useNotify } from "ra-core";
import React, { useState } from "react";
import { deliveryActions } from "../util";

interface Props {
  selectedIds?: number[];
  onComplete?: () => void;
}

const CannotPickMultipleDialog: React.FC<Props> = ({
  selectedIds,
  onComplete,
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState("");
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setReason("");
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await Promise.all(
        (selectedIds || []).map((id) =>
          dataProvider.update("deliveries", {
            id,
            data: {
              action: deliveryActions.CANNOT_START,
              issue_note: reason,
            },
            previousData: { id },
          })
        )
      );
      notify("Deliveries marked as cannot pick", "info");
      handleClose();
      onComplete?.(); // Refresh and clear selection
    } catch (e) {
      notify("Error updating deliveries", "warning");
    } finally {
      setLoading(false);
    }
  };

  const hasSelection = selectedIds && selectedIds.length > 0;

  return (
    <>
      <Button
        color="primary"
        onClick={handleOpen}
        size={"small"}
        disabled={!hasSelection}
        style={{ marginLeft: 8 }}
      >
        Cannot Pick {hasSelection ? `(${selectedIds.length})` : ""}
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="form-dialog-title">
          Cannot Pick Multiple Deliveries
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {hasSelection ? (
              <>
                There's an issue to pick these deliveries. For example, if the
                hospital does not want to release the items. This action will
                cancel the deliveries and Warehouse needs to allocate new boxes
                in the allocations.
                <br />
                You have <strong>{selectedIds.length} deliveries</strong> that
                will be marked as <b>Cannot Pick</b>.
              </>
            ) : (
              <>No deliveries selected.</>
            )}
            <Box py="10px">
              <TextField
                label="Reason"
                fullWidth
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                multiline
                rows={3}
              />
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            disabled={!hasSelection || loading}
          >
            {loading ? (
              <CircularProgress size={20} />
            ) : (
              `Mark as Cannot Pick (${selectedIds?.length || 0})`
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CannotPickMultipleDialog;
